import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { SupportedLanguageCode, LineOfBusiness, useConversationSDK, Chat } from '@spotify-internal/pillow-talk-too';

import { PillowTalkConfig, Vertical } from 'utils/vertical';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { CHAT_BOT_CLIENT_ID } from 'utils/config';
import { DEFAULT_LOCALE } from 'utils/i18n/localeConfig';
import { isSupportedMessagingLocale } from 'utils/market/market';

import { DEFAULT_STATUS, MessagingAvailabilityReason } from './types';
import { MessagingContext } from './MessagingContext';

interface PillowTalkMessagingProviderProps {
  vertical: Vertical;
  lineOfBusiness: LineOfBusiness;
  locale: string;
  redirectUri: string;
  children: ReactNode;
  showLoginDialog: boolean;
  allowFirstMessage: boolean;
}

const PillowTalkMessagingProvider = ({
  vertical,
  lineOfBusiness,
  locale,
  children,
  redirectUri,
  showLoginDialog,
  allowFirstMessage,
}: PillowTalkMessagingProviderProps) => {
  const shouldSetStatus = useRef(true);

  const [status, setStatus] = useState(DEFAULT_STATUS);

  const language = locale as SupportedLanguageCode;
  const { startConversation, conversation, sdkStatus } = useConversationSDK({
    clientId: CHAT_BOT_CLIENT_ID,
    language,
    redirectUri,
    lineOfBusiness,
  });

  useEffect(() => {
    const getStatus = async () => {
      const { availabilityStatus } = await sdkStatus;
      if (availabilityStatus === 'available') {
        const resolution = MessagingAvailabilityReason.AVAILABLE;
        setStatus({ isAvailable: true, reason: resolution });
        sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
      } else {
        const resolution = MessagingAvailabilityReason.PILLOW_TALK_ERROR;
        setStatus({ isAvailable: false, reason: resolution });
        sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
      }
    };
    if (!shouldSetStatus.current) {
      return;
    }
    shouldSetStatus.current = false;
    getStatus();
  }, [sdkStatus, vertical, shouldSetStatus]);

  const value = {
    status,
    conversation,
    startMessaging: status.isAvailable
      ? async () => {
          try {
            await startConversation({ showLoginDialog, allowFirstMessage });
            sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_START, { vertical });
          } catch (err) {
            captureException(err);
          }
        }
      : undefined,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
      {status.isAvailable && <Chat conversation={conversation} />}
    </MessagingContext.Provider>
  );
};

interface Props {
  children: React.ReactNode;
  locale: string;
  configurePillowTalk: PillowTalkConfig;
  vertical: Vertical;
  messagingLocales: Readonly<Array<string>>;
}

export const PillowTalkWrapper = ({ children, locale, configurePillowTalk, vertical, messagingLocales }: Props) => {
  const { lineOfBusiness, redirectUri, showLoginDialog } = configurePillowTalk;

  const language = isSupportedMessagingLocale(locale, messagingLocales) ? locale : DEFAULT_LOCALE;
  const allowFirstMessage = vertical === Vertical.CONSUMER;

  return (
    <PillowTalkMessagingProvider
      vertical={vertical}
      lineOfBusiness={lineOfBusiness}
      locale={language}
      redirectUri={redirectUri}
      showLoginDialog={showLoginDialog}
      allowFirstMessage={allowFirstMessage}
    >
      {children}
    </PillowTalkMessagingProvider>
  );
};
